import { Head } from "@inertiajs/react"
import { useMemo } from "react"
import { PageLayout } from "@/components/VocationalTest/PageLayout"
import { calculateMicroProfile, getPrimaryArea } from "@/data/microProfiles"
import type { EnneagramType, ProfileData } from "@/types/vocational"
import type { AdvancedScores } from "@/types/advancedVocational"
import logo from "@/assets/logo_transparente.png"

interface Props {
  user: {
    id: number
    name: string | null
    email: string
  }
  basic_result: {
    tipo_eneagrama?: number | null
    area_vocacional?: string | null
    pontuacoes_eneagrama?: Record<string, number> | null
    respostas?: Record<string, number> | null
    fim_at?: string | null
  }
  advanced_result: {
    microperfil?: string | null
    pontuacoes?: Record<string, number> | null
    respostas?: Record<string, number> | null
    fim_at?: string | null
  }
  profiles: ProfileData[]
}

const BRAND = {
  blueDeep: "#102A58",
  blue: "#1A4283",
  orange: "#ED7723",
  yellow: "#F9A646",
  gray: "#696667"
}

function buildAdvancedScores(pontuacoes?: Record<string, number> | null): AdvancedScores {
  const scores: AdvancedScores = { H: 0, E: 0, B: 0, A: 0, G: 0, C: 0 }
  if (!pontuacoes) return scores

  Object.entries(pontuacoes).forEach(([key, value]) => {
    const metric = key.toUpperCase()
    if (metric in scores) {
      scores[metric as keyof AdvancedScores] = Number(value) || 0
    }
  })

  return scores
}

function computeDominantType(
  counts: Record<EnneagramType, number>,
  forcedType?: number | null
): EnneagramType | null {
  if (forcedType && forcedType >= 1 && forcedType <= 9) return forcedType as EnneagramType

  const sorted = (Object.entries(counts) as [string, number][])
    .map(([type, count]) => [Number(type) as EnneagramType, count] as [EnneagramType, number])
    .sort((a, b) => b[1] - a[1])
  const top = sorted[0]
  return top && top[1] > 0 ? top[0] : null
}

function computeCounts(pontuacoes?: Record<string, number> | null, respostas?: Record<string, number> | null) {
  const counts: Record<EnneagramType, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 }
  if (pontuacoes && Object.keys(pontuacoes).length > 0) {
    Object.entries(pontuacoes).forEach(([key, value]) => {
      const type = Number(key) as EnneagramType
      if (type >= 1 && type <= 9) counts[type] = Number(value) || 0
    })
    return counts
  }
  if (respostas && Object.keys(respostas).length > 0) {
    Object.values(respostas).forEach((value) => {
      const type = Number(value) as EnneagramType
      if (type >= 1 && type <= 9) counts[type] += 1
    })
  }
  return counts
}

function formatDate(value?: string | null) {
  if (!value) return null
  const date = new Date(value)
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "long" }).format(date)
}

export default function AdvancedTestResults({ user, basic_result, advanced_result, profiles }: Props) {
  const counts = useMemo(
    () => computeCounts(basic_result.pontuacoes_eneagrama, basic_result.respostas),
    [basic_result.pontuacoes_eneagrama, basic_result.respostas]
  )
  const dominantType = useMemo(
    () => computeDominantType(counts, basic_result.tipo_eneagrama),
    [counts, basic_result.tipo_eneagrama]
  )
  const profileLookup = useMemo(() => {
    const lookup: Record<number, ProfileData> = {}
    profiles.forEach((p) => { lookup[p.tipo] = p })
    return lookup
  }, [profiles])
  const profileData = dominantType ? profileLookup[dominantType] : null

  const scores = useMemo(() => buildAdvancedScores(advanced_result.pontuacoes), [advanced_result.pontuacoes])
  const microProfile = useMemo(() => calculateMicroProfile(scores), [scores])
  const primaryArea = useMemo(() => getPrimaryArea(scores), [scores])

  const behavioralTendency = useMemo(() => {
    if (scores.B > scores.E && scores.B > scores.A) return "Cuidador e empático"
    if (scores.E > scores.H && scores.E > scores.B) return "Analítico e lógico"
    if (scores.A > scores.E && scores.A > scores.B) return "Criativo e expressivo"
    if (scores.G > scores.A && scores.G > scores.B) return "Organizador e estratégico"
    if (scores.H > scores.E) return "Comunicativo e inspirador"
    return "Multifacetado e adaptável"
  }, [scores])

  const completedAt = formatDate(advanced_result.fim_at)

  if (!profileData || !dominantType) {
    return (
      <>
        <Head title="Resultado Avançado" />
        <PageLayout>
          <div className="w-full max-w-2xl bg-white/90 rounded-3xl shadow-xl p-10 text-center">
            <img src={logo} alt="25Segundos" className="h-20 mx-auto mb-6" />
            <h1 className="text-2xl font-bold" style={{ color: BRAND.blueDeep }}>
              Resultado avançado indisponível
            </h1>
            <p className="mt-4 text-sm text-muted-foreground">
              Verifique se o teste avançado foi concluído.
            </p>
          </div>
        </PageLayout>
      </>
    )
  }

  return (
    <>
      <Head title="Resultado Avançado" />
      <style>{`
        @media print {
          body { background: #fff !important; }
          .print-hidden { display: none !important; }
          .print-clean { box-shadow: none !important; background: #fff !important; }
          .print-break { break-after: page; page-break-after: always; }
        }
      `}</style>
      <PageLayout>
        <div className="w-full max-w-6xl space-y-8">
          <div className="print-hidden flex items-center justify-end">
            <button
              type="button"
              className="rounded-full border border-gray-200 px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition"
              onClick={() => window.print()}
            >
              Imprimir ou salvar em PDF
            </button>
          </div>

          <section className="print-clean bg-white/95 rounded-3xl shadow-xl p-8 md:p-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center gap-4">
                <img src={logo} alt="25Segundos" className="h-20" />
                <div>
                  <p className="text-sm uppercase tracking-[0.2em]" style={{ color: BRAND.gray }}>
                    Orientação Profissional 25Segundos
                  </p>
                  <h1 className="text-3xl md:text-4xl font-bold" style={{ color: BRAND.blueDeep }}>
                    Resultado Avançado
                  </h1>
                  <p className="text-sm text-muted-foreground mt-2">
                    {user.name || user.email}
                    {completedAt ? ` · Concluído em ${completedAt}` : ""}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl px-6 py-4 text-center" style={{ background: "#F6F7FB" }}>
                <p className="text-xs uppercase tracking-[0.3em]" style={{ color: BRAND.orange }}>
                  Microperfil
                </p>
                <p className="text-lg font-semibold mt-1" style={{ color: BRAND.blueDeep }}>
                  {microProfile.name}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Tendência: {behavioralTendency}
                </p>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-[#EDF0F7] p-6 min-h-[240px]">
                <p className="text-xs uppercase tracking-[0.25em]" style={{ color: BRAND.orange }}>
                  Base do Perfil
                </p>
                <h2 className="text-2xl font-semibold mt-2" style={{ color: BRAND.blueDeep }}>
                  Tipo {dominantType} · {profileData.titulo}
                </h2>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                  {profileData.descricao_curta}
                </p>
              </div>

              <div className="rounded-2xl border border-[#EDF0F7] p-6 min-h-[240px]">
                <p className="text-xs uppercase tracking-[0.25em]" style={{ color: BRAND.orange }}>
                  Área Principal
                </p>
                <h2 className="text-2xl font-semibold mt-2" style={{ color: BRAND.blueDeep }}>
                  {primaryArea}
                </h2>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                  {microProfile.description}
                </p>
              </div>
            </div>
          </section>

          <section className="print-clean bg-white/95 rounded-3xl shadow-xl p-8 md:p-10">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.25em]" style={{ color: BRAND.orange }}>
                  Mapa de Competências
                </p>
                <h2 className="text-2xl font-semibold" style={{ color: BRAND.blueDeep }}>
                  Suas forças em detalhe
                </h2>
              </div>
              <span className="text-sm text-muted-foreground">
                Pontuações avançadas
              </span>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {([
                { key: "H", label: "Humanas e Comunicação" },
                { key: "E", label: "Exatas e Lógica" },
                { key: "B", label: "Biológicas e Cuidado" },
                { key: "A", label: "Artes e Criatividade" },
                { key: "G", label: "Gestão e Negócios" },
                { key: "C", label: "Conservação e Detalhe" }
              ] as Array<{ key: keyof AdvancedScores; label: string }>).map((item) => (
                <div key={item.key} className="rounded-2xl border border-[#EDF0F7] p-5 min-h-[140px]">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold" style={{ color: BRAND.blueDeep }}>
                      {item.label}
                    </h3>
                    <span className="text-xl font-bold" style={{ color: BRAND.blue }}>
                      {scores[item.key]}
                    </span>
                  </div>
                  <div className="mt-4 h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.min(100, scores[item.key] * 10)}%`,
                        background: BRAND.orange
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="print-clean bg-white/95 rounded-3xl shadow-xl p-8 md:p-10">
            <p className="text-xs uppercase tracking-[0.25em]" style={{ color: BRAND.orange }}>
              Direcionamento Profissional
            </p>
            <h2 className="text-2xl font-semibold mt-2" style={{ color: BRAND.blueDeep }}>
              {microProfile.name}
            </h2>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              {microProfile.description}
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-[#EDF0F7] p-5 min-h-[220px]">
                <h3 className="text-sm font-semibold uppercase tracking-[0.25em]" style={{ color: BRAND.blue }}>
                  Características-chave
                </h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {microProfile.characteristics}
                </p>
              </div>
              <div className="rounded-2xl border border-[#EDF0F7] p-5 min-h-[220px]">
                <h3 className="text-sm font-semibold uppercase tracking-[0.25em]" style={{ color: BRAND.blue }}>
                  Ambiente ideal
                </h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {microProfile.environment}
                </p>
              </div>
            </div>

            <div className="mt-8 rounded-2xl border border-[#EDF0F7] p-5">
              <h3 className="text-sm font-semibold uppercase tracking-[0.25em]" style={{ color: BRAND.blue }}>
                Profissões recomendadas
              </h3>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-muted-foreground">
                {microProfile.careers.map((career) => (
                  <div key={career} className="flex items-start gap-2">
                    <span className="mt-1 h-2 w-2 rounded-full" style={{ background: BRAND.yellow }} />
                    <span>{career}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </PageLayout>
    </>
  )
}
